import { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
import { useAppDispatch, useAppSelector } from '@/app/store';
import {
  setBrandCreativeModal,
  setCreativeDetailsModal,
} from '../creativesSlice';
import { CustomTable } from '@/shared/ui/Table';
import { handleSnackbar } from '@/shared/ui';
import {
  R2UploadService,
  autoDetectAspectRatio,
  getVideoMeta,
  getImageMeta,
  validateFileSizes,
} from '@/shared/utils';
import { sanitizeFileName } from '@/shared/utils/format';
import { creativesAPI } from '../creativesAPI';
import { useEditCreativeAssetMutation } from '../hooks/useCreativesQueries';

const fileHeaders = [
  { id: 'media', label: 'Media', type: 'image', sortable: false },
  { id: 'name', label: 'Name', sortable: false },
  { id: 'type', label: 'Type', sortable: false },
  { id: 'duration', label: 'Duration', sortable: false },
  { id: 'dimension', label: 'Dimension', sortable: false },
  { id: 'aspectRatio', label: 'Aspect Ratio', sortable: false },
  { id: 'addedOn', label: 'Added On', sortable: true },
  { id: 'actions', label: 'Actions', type: 'actions', sortable: false },
];

const BrandCreativeModal = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const { open, selectedBrand } = useAppSelector(
    (state: any) =>
      state.dooh.creative?.brandCreativeModal ?? {
        open: false,
        selectedBrand: null,
      }
  );

  useEffect(() => {
    setPage(0);
    setSearch('');
  }, [selectedBrand?.id]);

  const videoSizeLimit =
    Number(
      useAppSelector(
        (state: any) => state.auth.user?.doohConfig?.videoCreativeLimit
      )
    ) || 50;
  const imageSizeLimit =
    Number(
      useAppSelector(
        (state: any) => state.auth.user?.doohConfig?.imageCreativeLimit
      )
    ) || 5;
  const sizeLimits = {
    imageMaxBytes: imageSizeLimit * 1024 * 1024,
    videoMaxBytes: videoSizeLimit * 1024 * 1024,
  };

  const editMutation = useEditCreativeAssetMutation();

  const rawFiles = selectedBrand?.files ?? [];

  const handleClose = () => {
    dispatch(setBrandCreativeModal({ open: false, selectedBrand: null }));
  };

  const handleViewFile = (file: any) => {
    dispatch(
      setCreativeDetailsModal({
        open: true,
        selectedCreative: { ...selectedBrand, files: [file] },
      })
    );
  };

  const handleAddCreativeClick = () => {
    fileInputRef.current?.click();
  };

  const refreshSelectedBrand = async () => {
    const updatedBrand = await creativesAPI.getCreativeFolderById(
      selectedBrand.id
    );
    dispatch(
      setBrandCreativeModal({ open: true, selectedBrand: updatedBrand })
    );
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length > 30) {
      dispatch(
        handleSnackbar({
          open: true,
          message: 'You can upload a maximum of 30 files at once.',
          severity: 'error',
          autoHideDuration: 4000,
        })
      );
      e.target.value = '';
      return;
    }

    setUploading(true);

    const { validFiles, oversizedFilenames } = validateFileSizes(
      Array.from(files),
      sizeLimits
    );

    if (oversizedFilenames.length > 0) {
      dispatch(
        handleSnackbar({
          open: true,
          message: `Files too large (max ${imageSizeLimit}MB for images, ${videoSizeLimit}MB for videos): ${oversizedFilenames.join(', ')}`,
          severity: 'error',
          autoHideDuration: 5000,
        })
      );
    }

    if (validFiles.length === 0) {
      setUploading(false);
      e.target.value = '';
      return;
    }

    try {
      const metaFailedFiles: string[] = [];
      const fileMetadata = (
        await Promise.allSettled(
          validFiles.map(async file => {
            const isVideo = file.type.startsWith('video/');
            let duration = 0;
            let width = 0;
            let height = 0;

            if (isVideo) {
              const meta = await getVideoMeta(file);
              duration = meta.duration;
              [width, height] = meta.resolution.split('x').map(Number);
            } else {
              const meta = await getImageMeta(file);
              [width, height] = meta.resolution.split('x').map(Number);
            }

            const path = `creatives/${selectedBrand.id}/${Date.now()}_${sanitizeFileName(file.name)}`;
            return { file, path, isVideo, duration, width, height };
          })
        )
      ).flatMap((result, i) => {
        if (result.status === 'rejected') {
          metaFailedFiles.push(validFiles[i].name);
          return [];
        }
        return [result.value];
      });

      if (metaFailedFiles.length > 0) {
        dispatch(
          handleSnackbar({
            open: true,
            message: `Could not read metadata for: ${metaFailedFiles.join(', ')}. The file(s) may be corrupted.`,
            severity: 'error',
            autoHideDuration: 5000,
          })
        );
      }

      if (fileMetadata.length === 0) {
        setUploading(false);
        e.target.value = '';
        return;
      }

      // Phase 2: upload all files in one batch (single upload-link call + batched PUTs)
      const publicUrls = await R2UploadService.uploadFiles(
        fileMetadata.map(({ file, path }) => ({ file, storagePath: path }))
      );

      const preparedFiles = fileMetadata.map(
        ({ file, isVideo, duration, width, height }, idx) => ({
          name: file.name.replace(/\.[^/.]+$/, ''),
          aspectRatio: autoDetectAspectRatio(width, height),
          src: publicUrls[idx],
          type: isVideo ? 'VIDEO' : 'IMAGE',
          resolution: { width, height },
          sizeMb: parseFloat((file.size / (1024 * 1024)).toFixed(2)),
          duration,
        })
      );

      // Phase 3: single ADD API call with all files in one payload.
      const response = await editMutation.mutateAsync({
        brandId: selectedBrand.id,
        body: {
          action: 'ADD',
          assetId: null,
          data: {
            asset: {
              name: selectedBrand?.name ?? '',
              description: '',
              duration: Math.max(...preparedFiles.map(f => f.duration), 0),
              tags: [],
            },
            files: preparedFiles,
          },
        },
      });

      dispatch(
        handleSnackbar({
          open: true,
          message: response.message,
          severity: 'success',
          autoHideDuration: 3000,
        })
      );

      await refreshSelectedBrand();
    } catch (err: any) {
      dispatch(
        handleSnackbar({
          open: true,
          message:
            err instanceof Error ? err.message : 'Failed to upload files',
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteFileClick = (file: any) => {
    setDeleteConfirm(file);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const response = await editMutation.mutateAsync({
        brandId: selectedBrand.id,
        body: { action: 'DELETE', assetId: [deleteConfirm.id], data: null },
      });
      dispatch(
        handleSnackbar({
          open: true,
          message: response.message,
          severity: 'success',
          autoHideDuration: 3000,
        })
      );
      await refreshSelectedBrand();
    } catch (err: any) {
      dispatch(
        handleSnackbar({
          open: true,
          message: err instanceof Error ? err.message : 'Failed to delete file',
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
    } finally {
      setDeleteConfirm(null);
    }
  };

  const rows = rawFiles.map((file: any, index: number) => {
    const formatDate = (dateValue: any) => {
      if (!dateValue || dateValue === '-') return '-';
      let date = dayjs.utc(dateValue);
      if (!date.isValid()) date = dayjs.utc(dateValue, 'DD-MM-YYYY HH:mm:ss');
      return date.isValid()
        ? date.tz('Asia/Kolkata').format('D MMM, YYYY h:mm A')
        : '-';
    };

    const resWidth = file.resolution_width;
    const resHeight = file.resolution_height;
    const dimension = resWidth && resHeight ? `${resWidth}x${resHeight}` : '-';
    const duration = file.type === 'IMAGE' ? 0 : file.duration || 0;

    return {
      ...file,
      id: file.id || `file-${index}`,
      media: file,
      name: file.name || selectedBrand?.name || '-',
      type: file.type || '-',
      duration,
      dimension,
      aspectRatio: file.aspectRatio || '-',
      addedOn: formatDate(file.created_at || file.createdAt),
    };
  });

  const filteredRows = search.trim()
    ? rows.filter((r: any) => {
        const q = search.trim().toLowerCase();
        return (
          (r.name || '').toLowerCase().includes(q) ||
          (r.type || '').toLowerCase().includes(q) ||
          (r.dimension || '').toLowerCase().includes(q) ||
          (r.aspectRatio || '').toLowerCase().includes(q)
        );
      })
    : rows;

  const handleThumbnailClick = (srcOrRow: string | any) => {
    const src = typeof srcOrRow === 'string' ? srcOrRow : srcOrRow?.src;
    const matchingRow = rows.find((r: any) => r.src === src);
    if (matchingRow) handleViewFile(matchingRow);
  };

  const isLoading = uploading || editMutation.isPending;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.mp4"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" component="span">
              {selectedBrand?.name || 'Brand Creatives'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max 30 creatives per upload
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddIcon />
                )
              }
              size="small"
              onClick={handleAddCreativeClick}
              disabled={isLoading}
            >
              {isLoading ? 'Uploading...' : 'Add Creative'}
            </Button>
            <IconButton onClick={handleClose} size="small" disabled={isLoading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            placeholder="Search by name, type, dimension, or aspect ratio"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredRows.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4 }}
            >
              {rows.length === 0
                ? 'No creative files found for this brand.'
                : 'No results match your search.'}
            </Typography>
          ) : (
            <CustomTable
              columns={fileHeaders}
              rows={filteredRows.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={n => {
                setRowsPerPage(n);
                setPage(0);
              }}
              totalItems={filteredRows.length}
              paginationPosition="inline"
              actions={[{ label: 'delete', disabled: isLoading }]}
              showImagePreview={handleThumbnailClick}
              viewAction={handleViewFile}
              deleteAction={handleDeleteFileClick}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Per-file delete confirmation */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirm?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm(null)}
            disabled={editMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={editMutation.isPending}
          >
            {editMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BrandCreativeModal;
